import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { GeneralParameters } from 'src/app/constants/general-parameters-constans';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { GeneralParameterEnums } from 'src/app/enum/general-parameter-enums';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { RemissionOrderStateEnum } from 'src/app/enum/remission-order-state-enum';
import { RemissionStateEnums } from 'src/app/enum/remission-state-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { RemissionChartModel } from 'src/app/models/charts/rental/remission-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceRemissionTableModel } from 'src/app/models/tables/rental/invoice-remission-table.model';
import { RemissionDetailTableModel } from 'src/app/models/tables/rental/remission-detail-table.model';
import { RemissionFromOrderTableModel } from 'src/app/models/tables/rental/remission-from-order-table.model';
import { RemissionTableModel } from 'src/app/models/tables/rental/remission-table.model';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { RemissionDetailService } from 'src/app/services/rental/remission-detail.service';
import { RemissionFromOrderService } from 'src/app/services/rental/remission-from-order.service';
import { RemissionService } from 'src/app/services/rental/remission.service';
import { OrderDataComponent } from '../../remission-detail/order-data/order-data.component';

const RemissionIndicatorsConstants = {
  states: [
    { id: RemissionStateEnums.open, label: 'Abiertas', color: '#EF4444', isApp: false },
    { id: RemissionStateEnums.initialClose, label: 'Cierre Inicial', color: '#14B8A6', isApp: true },
    { id: RemissionStateEnums.transit, label: 'Tránsito', color: '#CA8A04', isApp: true },
    { id: RemissionStateEnums.printed, label: 'Impresas', color: '#3B82F6', isApp: false },
    { id: RemissionStateEnums.finalClose, label: 'Cierre Final', color: '#198754', isApp: false },
    { id: RemissionStateEnums.adjustments, label: 'Ajustes', color: '#475569', isApp: true },
    { id: RemissionStateEnums.annulled, label: 'Anuladas', color: '#FB923C', isApp: false }
  ]
};

@Component({
  selector: 'app-remission',
  templateUrl: './remission.component.html',
  styleUrls: ['./remission.component.scss'],
  standalone: false
})
export class RemissionComponent implements OnInit {

  @ViewChild('orderDataModal') orderDataModalComponent?: CustomModalComponent | null = null;

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  componentOrderData = OrderDataComponent;
  detailData: PagedResponseModel<RemissionDetailTableModel> = new PagedResponseModel<RemissionDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<RemissionTableModel> = new PagedResponseModel<RemissionTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicators: RemissionChartModel = new RemissionChartModel();
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(this.indicators);
  indicatorsRequestId: number = 0;
  inputsForOrderDataModal$ = new BehaviorSubject<any>({
    orderId: 0
  });
  invoiceData: PagedResponseModel<InvoiceRemissionTableModel> = new PagedResponseModel<InvoiceRemissionTableModel>();
  invoiceHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  listObservers: Array<Subscription> = new Array<Subscription>();
  listRemissionOrderState: ColumnResponseModel = new ColumnResponseModel();
  loadingIndicators: boolean = false;
  nameWindow: string = 'Remisiones';
  origin: WindowEnums = WindowEnums.Remission;
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  remissionFromOrderActions: TableAction[] = [
    {
      label: 'Datos del pedido',
      icon: 'pi pi-info-circle',
      action: 'order-data'
    }
  ];
  remissionFromOrderData: PagedResponseModel<RemissionFromOrderTableModel> = new PagedResponseModel<RemissionFromOrderTableModel>();
  remissionFromOrderHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  remissionOrderState: RemissionOrderStateEnum = RemissionOrderStateEnum.pending;
  swAppMovil: boolean = false;
  swOrderModule: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(private remissionService: RemissionService,
    private remissionDetailService: RemissionDetailService,
    private remissionFromOrderService: RemissionFromOrderService,
    private userAuthenticatedService: UserAuthenticatedService,
    private router: Router,
    private headerService: HeaderService,
    private title: Title) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('remission');
    filters ? this.filters = JSON.parse(filters) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllRemissionsWithPagination(),
      this.getGeneralParametersByCompany(),
      this.getIndicatorsForGeneralConsultation()
    ]);
    this.headerService.setTitle(this.nameWindow);
  }

  async ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
  }

  buildIndicatorCharts(indicators: RemissionChartModel): IndicatorChart[] {
    const states = RemissionIndicatorsConstants.states.filter(state => this.swAppMovil || !state.isApp);
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Remisiones por estado',
        labels: states.map(state => state.label),
        values: states.map(state => this.getIndicatorQuantity(indicators.states, state.id)),
        colors: states.map(state => state.color),
        total: indicators.total
      }
    ];
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.remissionDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Remisión Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las remisiones detalle.', error);
    }
  }

  async exportInvoiceForRemissionConsultation() {
    try {
      const blob = await firstValueFrom(this.remissionService.exportInvoiceForRemissionConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Remisión Facturas.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las facturas de remisiones detalle.', error);
    }
  }

  async exportRemissionFromOrderConsultation() {
    try {
      const blob = await firstValueFrom(this.remissionFromOrderService.exportRemissionFromOrderConsultation(this.filters, this.columnSort,
        this.ascendant, this.remissionOrderState, this.origin));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Pedidos Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los pedidos detalle.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const blob = await firstValueFrom(this.remissionService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant, timeZoneId));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Remisión.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar remisiones.', error);
    }
  }

  async getColumnsForRemissionFromOrderConsultation() {
    try {
      if (this.remissionFromOrderHeader.columns.length === 0) {
        this.remissionFromOrderHeader = await firstValueFrom(this.remissionFromOrderService.getColumnsForRemissionFromOrderConsultation());
      }
      this.listRemissionOrderState = this.remissionFromOrderHeader.filtersGlobal[0];
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.remissionDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.remissionService.getColumnsForGeneralConsultation());
      }
      this.listRemissionOrderState = new ColumnResponseModel;
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getColumnsForInvoiceConsultation() {
    try {
      if (this.invoiceHeader.length === 0) {
        this.invoiceHeader = await firstValueFrom(this.remissionService.getColumnsForInvoiceConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de facturas.', error);
    }
  }

  async getGeneralParametersByCompany() {
    const load = this.userAuthenticatedService.generalParameterObservable.subscribe(response => {
      this.swOrderModule = response.some(x => x.name === GeneralParameters.orderModule);
      this.swAppMovil = response.some(x => x.generalParameterId === GeneralParameterEnums.appMovil);
      this.indicatorCharts = this.buildIndicatorCharts(this.indicators);
    });
    this.listObservers.push(load);
  }

  async getAllInvoiceForRemissionsWithPagination() {
    try {
      this.invoiceData = await firstValueFrom(this.remissionService.getAllInvoiceForRemissionsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las facturas para las remisiones.', error);
    }
  }

  async getAllRemissionFromOrderWithPagination() {
    try {
      this.remissionFromOrderData = await firstValueFrom(this.remissionFromOrderService.getAllRemissionFromOrderWithPagination(this.page, this.limit, this.columnSort, this.ascendant,
        this.remissionOrderState, this.origin, this.filters));
    } catch (error) {
      console.error('Error al buscar las remisiones.', error);
    }
  }

  async getAllRemissionsWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.remissionService.getAllRemissionsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las remisiones.', error);
    }
  }

  async getAllRemissionDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.remissionDetailService.getAllRemissionDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las remisiones.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.remissionService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicators = indicators;
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de las remisiones.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.invoiceHeader.length = this.invoiceHeader.length === 1 ? 0 : this.invoiceHeader.length;
    this.remissionFromOrderHeader.columns.length = this.remissionFromOrderHeader.columns.length === 1 ? 0
      : this.remissionFromOrderHeader.columns.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllRemissionsWithPagination(),
          this.getIndicatorsForGeneralConsultation()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllRemissionDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForRemissionFromOrderConsultation(),
          this.getAllRemissionFromOrderWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 3:
        await Promise.all([
          this.getColumnsForInvoiceConsultation(),
          this.getAllInvoiceForRemissionsWithPagination()
        ]);
        this.invoiceData = {
          ...this.invoiceData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newRemission() {
    this.router.navigate(['/remission/register']);
  }

  onAction(event: { action: string, item?: RemissionTableModel | RemissionFromOrderTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/remission/register/${event.item.id}`]);
    }
    if (event.action === 'order-data' && event.item) {
      this.openOrderDataModal((event.item as RemissionFromOrderTableModel).orderId);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllRemissionsWithPagination();
        break;
      case 1:
        await this.getAllRemissionDetailsWithPagination();
        break;
      case 2:
        await this.getAllRemissionFromOrderWithPagination();
        break;
      case 3:
        await this.getAllInvoiceForRemissionsWithPagination();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportRemissionFromOrderConsultation();
        break;
      case 3:
        await this.exportInvoiceForRemissionConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  onRemissionOrderStateChange(value: number) {
    this.remissionOrderState = value;
  }

  openOrderDataModal(orderId: number) {
    this.inputsForOrderDataModal$.next({
      orderId: orderId
    });
    this.orderDataModalComponent?.show();
  }

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

}